import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IUserPreferenceInput } from '@shared/models/user';
import { HotjarService } from '@core/services/hotjar.service';
import { REVIEW_CBSA_PREFERENCES_MIN_DATE } from '@core/constants/auth';
import { IFilterCBSAs } from '@shared/models/filters';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser } from '@state/auth/auth.selectors';
import {
  setAuthModal,
  updateUserPreferenceAction,
  updateUserPreferenceActionSuccess,
  updateUserPreferenceActionFailure,
} from '@state/auth/auth.actions';

@UntilDestroy()
@Component({
  selector: 'app-review-cbsa-modal',
  templateUrl: './review-cbsa.component.html',
  styleUrls: ['./review-cbsa.component.scss'],
})
export class ReviewCbsaModalComponent implements OnInit {
  protected saving = false;
  protected preferenceId: string | undefined;
  protected userName = '';

  public reviewCbsaForm: FormGroup = this.formBuilder.group({
    cbsaPreferences: new FormControl('', [Validators.required]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastr: ToastrService,
    private actions$: Actions,
    private hotjarService: HotjarService
  ) {}

  ngOnInit() {
    this.hotjarService.callEvent('start_verify_identity');

    this.store
      .select(selectAuthUser)
      .pipe(take(2))
      .subscribe((user) => {
        if (user) {
          this.userName = user.firstName + ' ' + user.surname;
          if (user.cbsaPreferences) {
            this.reviewCbsaForm.controls['cbsaPreferences'].setValue(
              user.cbsaPreferences.map((cbsa) => ({
                type: cbsa.type,
                preference: cbsa.preference,
              }))
            );
          }
        }
      });

    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        if (
          user &&
          user.cbsaPreferences &&
          user.cbsaPreferences.length > 0 &&
          new Date(user.cbsaPreferences[0]?.updatedAt) >=
            REVIEW_CBSA_PREFERENCES_MIN_DATE
        ) {
          this.store.dispatch(setAuthModal({ authModal: undefined }));
        }
      });

    this.actions$
      .pipe(ofType(updateUserPreferenceActionSuccess), untilDestroyed(this))
      .subscribe(() => {
        this.saving = false;
        this.store.dispatch(setAuthModal({ authModal: undefined }));
      });

    this.actions$
      .pipe(ofType(updateUserPreferenceActionFailure), untilDestroyed(this))
      .subscribe(() => {
        this.saving = false;
      });
  }

  handleSubmit() {
    if (this.reviewCbsaForm.invalid) {
      this.reviewCbsaForm.markAllAsTouched();
      return;
    }

    this.store
      .select(selectAuthUser)
      .pipe(take(1))
      .subscribe((user) => {
        if (!user?.preference) {
          this.toastr.error(
            'User preference not found. Please try again later or contact support.',
            'Error'
          );
        } else {
          const newCbsaPreferences =
            this.reviewCbsaForm.value.cbsaPreferences.map(
              (cbsa: IFilterCBSAs) =>
                JSON.stringify({
                  type: cbsa.type.toLowerCase(),
                  preference: cbsa.preference,
                })
            );
          const input: IUserPreferenceInput['input'] = {
            roles: user.preference.roles || [],
            investmentStyles: user.preference.investmentStyles || [],
            rehabComfortabilities: user.preference.rehabComfortabilities || [],
            // map to json string the entities with id and full_name
            entities:
              user.entities && user.entities.length
                ? user.entities.map((entity) =>
                    JSON.stringify({
                      id: entity.entityId,
                      full_name: entity.fullName,
                    })
                  )
                : [],
            cbsaPreferences: newCbsaPreferences,
          };

          this.store.dispatch(
            updateUserPreferenceAction({ id: user.preference.id, input: input })
          );

          this.saving = true;
        }
      });
  }

  handleCbsaPreferences(values: IFilterCBSAs[]) {
    this.reviewCbsaForm.controls['cbsaPreferences'].setValue(values);
    this.reviewCbsaForm.controls['cbsaPreferences'].markAsTouched();
  }
}
